import React from "react"
import { matchPath, Redirect, useLocation } from "react-router-dom"
import { PageLoading } from "#/features/global-ui/page-layout"
import { type NavAvailability, useNavAvailability } from "#/services/feature-availability/useNavAvailability"
import { CarrotCollectiveRedirects, Paths } from "#/utils/Paths"

type FeatureRoute = {
  path: string
  match: "exact" | "prefix"
  gate: keyof NavAvailability["route"]
  except?: readonly string[]
}

// One list rather than a wrapper per route, so deep links, reloads and in-app clicks are all caught.
const FEATURE_ROUTES: readonly FeatureRoute[] = [
  { path: Paths.TELEMEDICINE_APPOINTMENTS, match: "exact", gate: "appointments" },
  // Config only, so it covers the enterprise Care Team's entry points too.
  { path: Paths.TELEMEDICINE, match: "prefix", gate: "appointments" },
  { path: "/messages/providers", match: "prefix", gate: "clinicians" },
  { path: "/carrot-rx", match: "exact", gate: "carrotRx" },
  {
    path: Paths.PROVIDER_FINDER,
    match: "prefix",
    gate: "providerFinder",
    // Menopause telemedicine pages that only live under the provider finder's URL.
    except: [Paths.TELEMEDICINE_FOR_MENOPAUSE_CARE, `${Paths.PROVIDER_FINDER}/telemedicine-for-menopause`]
  },
  { path: "/providerfinder", match: "prefix", gate: "providerFinder" },
  { path: Paths.EXCLUSIVE_DISCOUNTS, match: "exact", gate: "exclusiveDiscountsOffered" },
  ...CarrotCollectiveRedirects.map(({ from }) => ({
    path: from,
    match: "exact" as const,
    gate: "exclusiveDiscountsOffered" as const
  })),
  // The help center, for every program.
  { path: Paths.FAQS, match: "prefix", gate: "helpCenter" },
  { path: "/support", match: "exact", gate: "helpCenter" },
  { path: "/consumer/faq", match: "exact", gate: "helpCenter" }
]

// The router's own matcher, as in PaywallGate, so a prefix ends on a segment boundary.
function matches(pathname: string, path: string, exact: boolean): boolean {
  return matchPath(pathname, { path, exact }) !== null
}

function featureRouteFor(pathname: string): FeatureRoute | undefined {
  return FEATURE_ROUTES.find(
    ({ path, match, except = [] }) =>
      matches(pathname, path, match === "exact") && !except.some((excepted) => matches(pathname, excepted, false))
  )
}

// Loading holds, error renders (fail open), off redirects home.
export function FeatureRouteGate({ children }: { children: React.ReactNode }): JSX.Element {
  const { pathname } = useLocation()
  const { route } = useNavAvailability()
  const featureRoute = featureRouteFor(pathname)
  if (!featureRoute) return <>{children}</>

  const gate = route[featureRoute.gate]
  if (gate === "loading") return <PageLoading />
  if (gate === "off") return <Redirect to="/" />
  return <>{children}</>
}
